import { useCallback, useState } from 'react';

import { useAuth } from './useAuth';
import { NEW_MISSION_MAX, newMissionObjective, newMissionTitle } from '../lib/bureau/newMission';
import { notifyMissionsChanged } from '../lib/mission/missionsChanged';
import { supabase } from '../lib/supabase';

interface Envelope<T> {
  success?: boolean;
  error?: string;
  data?: T;
}

async function invoke<T>(fn: string, body: Record<string, unknown>): Promise<T | undefined> {
  const { data, error } = await supabase.functions.invoke<Envelope<T>>(fn, { body });
  if (error) throw error;
  if (data?.success === false) throw new Error(data.error ?? 'Action refusée');
  return data?.data;
}

/** What the mission is tied to — one link, the narrowest (the Edge keeps the first): its « Missions liées » show it. */
function relatedToOf(input: NewMissionInput) {
  if (input.opportunity) return { relatedTo: { type: 'opportunity', id: input.opportunity.id } };
  if (input.contact) return { relatedTo: { type: 'contact', id: input.contact.id } };
  if (input.account) return { relatedTo: { type: 'account', id: input.account.id } };
  return {};
}

export interface NewMissionInput {
  text: string;
  account: { id: string; name: string } | null;
  /** Asked from a contact's sheet: the mission is tied to the contact (its account follows by key). */
  contact?: { id: string; name: string } | null;
  /** Asked from a deal's sheet: the mission is tied to the deal (its account follows by key). */
  opportunity?: { id: string; name: string } | null;
  procedure: { id: string; slug: string; label: string } | null;
  driveFileIds: readonly string[];
}

/** Open a mission directly; context belongs to it. Classify it and attach the requested files. */
export function useNewMission() {
  const { user } = useAuth();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (input: NewMissionInput): Promise<{ source: 'crm_task'; sourceId: string } | null> => {
      if (!user?.id) {
        setError('Session introuvable : reconnecte-toi.');
        return null;
      }
      // Validate the complete objective before opening the mission.
      const objectiveLength = newMissionObjective(input).length;
      if (objectiveLength > NEW_MISSION_MAX) {
        setError(`Demande trop longue (${objectiveLength} caractères, ${NEW_MISSION_MAX} au plus) : raccourcis-la.`);
        return null;
      }
      setPending(true);
      setError(null);
      try {
        const started = await invoke<{ thread_id: string; source_id: string }>('work-agent-threads', {
          action: 'open',
          title: newMissionTitle(input.text),
          objective: newMissionObjective(input),
          ...relatedToOf(input),
        });
        const threadId = started?.thread_id;
        if (!threadId) throw new Error('La mission n’a pas été créée.');
        if (threadId && input.procedure) {
          await supabase.rpc('correct_mission_competence', { p_thread_id: threadId, p_competence_id: input.procedure.id });
        }
        if (threadId && input.driveFileIds.length > 0) {
          await invoke('work-agent-threads', {
            action: 'instruct',
            threadId,
            instruction: 'Les fichiers joints à ma demande, à lire avant de commencer.',
            driveFiles: [...input.driveFileIds],
          });
        }
        notifyMissionsChanged();
        return { source: 'crm_task', sourceId: started.source_id };
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return null;
      } finally {
        setPending(false);
      }
    },
    [user?.id],
  );

  return { create, pending, error };
}
